import React, { useState } from 'react';
import EtaDialog from './EtaDialog';
import DispatchDialog from './DispatchDialog';
import RejectionDialog from './RejectionDialog';
import CancelDialog from './CancelDialog';
import GoaDialog from './GoaDialog';
import EtaUpdateDialog from './EtaUpdateDialog';
import UnsuccessfulDialog from './UnsuccessfulDialog';
import Org1DriverEtaDecisionDialog from './Org1DriverEtaDecisionDialog';
import * as jobActionHandlers from '../../utils/jobActionHandlers';

/**
 * JobDialogManager handles all the job-related dialogs and their state
 */
const JobDialogManager = ({ 
  loading,
  currentUser, // Add currentUser here
  selectedJob, 
  setSelectedJob, 
  refreshJobs, 
  startLoading, // Add startLoading prop
  drivers, 
  trucks,
  vehicles,
  jobToReject,
  setJobToReject,
  jobToCancel,
  setJobToCancel,
  jobToMarkGOA,
  setJobToMarkGOA,
  jobToUpdateEta,
  setJobToUpdateEta,
  jobToMarkUnsuccessful,
  setJobToMarkUnsuccessful,
  etaDialogOpen,
  setEtaDialogOpen,
  dispatchDialogOpen,
  setDispatchDialogOpen,
  rejectDialogOpen,
  setRejectDialogOpen,
  cancelDialogOpen,
  setCancelDialogOpen,
  goaDialogOpen,
  setGoaDialogOpen,
  etaUpdateDialogOpen,
  setEtaUpdateDialogOpen,
  unsuccessfulDialogOpen,
  setUnsuccessfulDialogOpen,
  org1DriverEtaDecisionDialogOpen, // Add Org1 Driver ETA decision dialog state
  setOrg1DriverEtaDecisionDialogOpen,
  jobForOrg1DriverEtaDecision, // Add job for Org1 Driver ETA decision
  setJobForOrg1DriverEtaDecision,
  setNotification // Add setNotification prop
}) => {

  // Handle ETA dialog submit
  const handleEtaDialogSave = async (etaValue) => {
    await jobActionHandlers.handleEtaDialogSave(
      etaValue, 
      selectedJob, 
      startLoading,
      refreshJobs,
      setEtaDialogOpen,
      setSelectedJob,
      currentUser, // Pass currentUser here
      setNotification // Pass setNotification
    );
  };
  
  // Handle ETA update dialog submit
  const handleEtaUpdateSave = async (additionalEtaValue) => {
    await jobActionHandlers.handleEtaUpdateSave(
      additionalEtaValue,
      jobToUpdateEta,
      startLoading, // Pass startLoading
      refreshJobs,
      setEtaUpdateDialogOpen,
      setJobToUpdateEta,
      currentUser, // Pass currentUser here
      setNotification // Pass setNotification
    );
  };

  // Handle driver assignment
  const handleAssign = async (driver, truck) => {
    await jobActionHandlers.handleAssignDriverAndTruck(
      driver, 
      truck, 
      selectedJob, 
      startLoading, // Pass startLoading
      refreshJobs,
      setDispatchDialogOpen,
      setSelectedJob,
      vehicles,
      setNotification // Pass setNotification
    );
  };

  // Handle job rejection
  const handleSubmitRejection = async (rejectionReason) => {
    await jobActionHandlers.handleSubmitRejection(
      jobToReject,
      rejectionReason,
      startLoading, // Pass startLoading
      refreshJobs,
      setRejectDialogOpen,
      setJobToReject,
      setNotification // Pass setNotification
    );
  };

  // Handle job cancellation
  const handleCancelJob = async (cancellationReason) => {
    await jobActionHandlers.handleCancelJob(
      jobToCancel,
      cancellationReason,
      startLoading, // Pass startLoading
      refreshJobs,
      setCancelDialogOpen,
      setJobToCancel,
      setNotification // Pass setNotification
    );
  };

  // Handle GOA request
  const handleGoaRequest = async (goaReason) => {
    await jobActionHandlers.handleGoaRequest(
      jobToMarkGOA,
      goaReason,
      startLoading, // Pass startLoading
      refreshJobs,
      setGoaDialogOpen,
      setJobToMarkGOA,
      setNotification // Pass setNotification
    );
  };

  // Handle Report Unsuccessful
  const handleReportUnsuccessful = async (unsuccessfulReason) => {
    await jobActionHandlers.handleReportUnsuccessful(
      jobToMarkUnsuccessful,
      unsuccessfulReason,
      startLoading, // Pass startLoading
      refreshJobs,
      setUnsuccessfulDialogOpen,
      setJobToMarkUnsuccessful,
      setNotification // Pass setNotification
    );
  };
  
  // Handle Org1 Driver ETA Decision
  const handleOrg1DriverEtaDecision = async (updatedJob, decision, errorMessage) => {
    try {
      console.log(`[JobDialogManager] Handling Org1 driver ETA decision: ${decision}`, {
        jobId: jobForOrg1DriverEtaDecision?._id,
        hasUpdatedJob: !!updatedJob,
        hasError: !!errorMessage
      });
      
      startLoading();
      
      // Update UI
      setOrg1DriverEtaDecisionDialogOpen(false);
      setJobForOrg1DriverEtaDecision(null);
      
      // If we received an error message, show error notification and stop
      if (errorMessage) {
        console.log(`[JobDialogManager] ETA decision error: ${errorMessage}`);
        setNotification({
          open: true,
          message: `Error: ${errorMessage}`,
          severity: 'error'
        });
        return;
      }
      
      // Log the updated job from the backend
      if (updatedJob) {
        console.log(`[JobDialogManager] Updated job after ETA ${decision}:`, {
          id: updatedJob._id,
          status: updatedJob.status,
          driver: updatedJob.driver,
          driverId: updatedJob.driverId,
          eta: updatedJob.eta,
          pendingOrg1DriverETA: updatedJob.pendingOrg1DriverETA,
          automatedDispatchEnabled: updatedJob.automatedDispatchEnabled
        });
      }
      
      // Refresh jobs to get the updated data
      console.log(`[JobDialogManager] Refreshing jobs after ETA ${decision}`);
      await refreshJobs();
      
      // Show notification
      const successMessage = `${decision === 'accept' ? 'Accepted' : 'Rejected'} driver ETA successfully`;
      console.log(`[JobDialogManager] ${successMessage}`);
      setNotification({
        open: true,
        message: successMessage,
        severity: decision === 'accept' ? 'success' : 'info'
      });
    } catch (error) {
      console.error('[JobDialogManager] Error handling Org1 driver ETA decision:', error);
      console.error('[JobDialogManager] Error stack:', error.stack);
      setNotification({
        open: true,
        message: `Error: ${error.message || 'Failed to process ETA decision'}`,
        severity: 'error'
      });
    }
  };

  // Debug: Log props
  // console.log('JobDialogManager vehicles prop:', vehicles);

  return (
    <>
      {/* ETA Dialog */}
      <EtaDialog 
        open={etaDialogOpen} 
        onClose={() => setEtaDialogOpen(false)} 
        onSave={handleEtaDialogSave}
        loading={loading}
      />
      
      {/* Dispatch Dialog */}
      <DispatchDialog 
        open={dispatchDialogOpen} 
        onClose={() => setDispatchDialogOpen(false)} 
        onAssign={handleAssign}
        loading={loading}
        drivers={drivers}
        trucks={trucks}
        vehicles={vehicles}
        selectedJob={selectedJob} // Pass selectedJob to DispatchDialog
      />
      
      {/* Rejection Dialog */}
      <RejectionDialog 
        open={rejectDialogOpen} 
        onClose={() => setRejectDialogOpen(false)} 
        onSubmit={handleSubmitRejection}
        loading={loading}
      />
      
      {/* Cancellation Dialog */}
      <CancelDialog 
        open={cancelDialogOpen} 
        onClose={() => setCancelDialogOpen(false)} 
        onSubmit={handleCancelJob}
        loading={loading}
      />
      
      {/* GOA Dialog */}
      <GoaDialog 
        open={goaDialogOpen} 
        onClose={() => setGoaDialogOpen(false)} 
        onSubmit={handleGoaRequest}
        loading={loading}
      />
      
      {/* ETA Update Dialog */}
      <EtaUpdateDialog 
        open={etaUpdateDialogOpen} 
        onClose={() => setEtaUpdateDialogOpen(false)} 
        onSave={handleEtaUpdateSave}
        loading={loading}
        currentEta={jobToUpdateEta?.eta}
      />
      
      {/* Unsuccessful Dialog */}
      <UnsuccessfulDialog 
        open={unsuccessfulDialogOpen} 
        onClose={() => setUnsuccessfulDialogOpen(false)} 
        onSubmit={handleReportUnsuccessful}
        loading={loading}
      />
      
      {/* Org1 Driver ETA Decision Dialog */}
      <Org1DriverEtaDecisionDialog
        open={org1DriverEtaDecisionDialogOpen}
        onClose={() => setOrg1DriverEtaDecisionDialogOpen(false)}
        job={jobForOrg1DriverEtaDecision}
        onDecision={handleOrg1DriverEtaDecision}
        loading={loading}
      />
    </>
  );
};

// Export the component and dialog state hooks for use in Jobs component
export {
  JobDialogManager
};

// Export dialog opener functions
export const useJobDialogs = () => {
  const [etaDialogOpen, setEtaDialogOpen] = useState(false);
  const [dispatchDialogOpen, setDispatchDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [goaDialogOpen, setGoaDialogOpen] = useState(false);
  const [etaUpdateDialogOpen, setEtaUpdateDialogOpen] = useState(false);
  const [unsuccessfulDialogOpen, setUnsuccessfulDialogOpen] = useState(false);
  const [org1DriverEtaDecisionDialogOpen, setOrg1DriverEtaDecisionDialogOpen] = useState(false);
  
  // State for selected job and related job-specific data
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobToReject, setJobToReject] = useState(null);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [jobToMarkGOA, setJobToMarkGOA] = useState(null);
  const [jobToUpdateEta, setJobToUpdateEta] = useState(null);
  const [jobToMarkUnsuccessful, setJobToMarkUnsuccessful] = useState(null);
  const [jobForOrg1DriverEtaDecision, setJobForOrg1DriverEtaDecision] = useState(null);
  
  return {
    etaDialogOpen, setEtaDialogOpen,
    dispatchDialogOpen, setDispatchDialogOpen,
    rejectDialogOpen, setRejectDialogOpen,
    cancelDialogOpen, setCancelDialogOpen,
    goaDialogOpen, setGoaDialogOpen,
    etaUpdateDialogOpen, setEtaUpdateDialogOpen,
    unsuccessfulDialogOpen, setUnsuccessfulDialogOpen,
    org1DriverEtaDecisionDialogOpen, setOrg1DriverEtaDecisionDialogOpen,
    selectedJob, setSelectedJob,
    jobToReject, setJobToReject,
    jobToCancel, setJobToCancel,
    jobToMarkGOA, setJobToMarkGOA,
    jobToUpdateEta, setJobToUpdateEta,
    jobToMarkUnsuccessful, setJobToMarkUnsuccessful,
    jobForOrg1DriverEtaDecision, setJobForOrg1DriverEtaDecision
  };
};
